/**
 * Community Garden — Secure Token Storage (RISK-006)
 * 
 * Uses memory-only access tokens and httpOnly cookies for reload continuity.
 */

export interface TokenPair {
  accessToken: string;
  expiresAt: string;
  tokenId: string;
}

interface TokenState {
  accessToken: string | null;
  expiresAt: string | null;
  tokenId: string | null;
  refreshPromise: Promise<TokenPair> | null;
}

// Memory-only storage - cleared on page reload.
const memoryStore: TokenState = {
  accessToken: null,
  expiresAt: null,
  tokenId: null,
  refreshPromise: null
};

// Buffer time before expiry to trigger refresh (5 minutes)
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

export class SecureTokenStorage {
  /**
   * Store tokens in memory only. Refresh token is handled via httpOnly cookie.
   */
  static setTokens(tokenPair: TokenPair): void {
    memoryStore.accessToken = tokenPair.accessToken;
    memoryStore.expiresAt = tokenPair.expiresAt;
    memoryStore.tokenId = tokenPair.tokenId;
  }

  /**
   * Get current access token if valid.
   * Returns null if expired or not present.
   */
  static getAccessToken(): string | null {
    if (!memoryStore.accessToken || !memoryStore.expiresAt) {
      return null;
    }

    // Check if token is expired
    if (new Date(memoryStore.expiresAt) < new Date()) {
      return null;
    }

    return memoryStore.accessToken;
  }

  /**
   * Check if token needs refresh (within buffer window)
   */
  static needsRefresh(): boolean {
    if (!memoryStore.expiresAt) {
      return false;
    }

    const expiryTime = new Date(memoryStore.expiresAt).getTime();
    const now = Date.now();
    
    return expiryTime - now < REFRESH_BUFFER_MS;
  }

  /**
   * Check if token is expired
   */
  static isExpired(): boolean {
    if (!memoryStore.expiresAt) {
      return true;
    }
    return new Date(memoryStore.expiresAt) < new Date();
  }

  /**
   * Clear all tokens from memory
   */
  static clear(): void {
    memoryStore.accessToken = null;
    memoryStore.expiresAt = null;
    memoryStore.tokenId = null;
    memoryStore.refreshPromise = null;
  }

  /**
   * Get token ID for rotation tracking
   */
  static getTokenId(): string | null {
    return memoryStore.tokenId;
  }

  /**
   * Attempt to refresh tokens via API.
   * Uses the HTTP-only session cookie when present and rotates the server session.
   */
  static async refreshTokens(): Promise<TokenPair | null> {
    // Prevent concurrent refresh requests
    if (memoryStore.refreshPromise) {
      return memoryStore.refreshPromise;
    }

    const refreshPromise = this.performRefresh();
    memoryStore.refreshPromise = refreshPromise;

    try {
      const result = await refreshPromise;
      return result;
    } finally {
      memoryStore.refreshPromise = null;
    }
  }

  private static async performRefresh(): Promise<TokenPair | null> {
    try {
      // Cookie-backed endpoint returns the current server session.
      const response = await fetch('/auth/refresh', {
        method: 'POST',
        credentials: 'include', // Required to send httpOnly cookies
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 204) {
        return null;
      }

      if (!response.ok) {
        if (response.status === 401) {
          // Refresh token expired or invalid - clear and require re-auth
          this.clear();
          return null;
        }
        return null;
      }

      const data = await response.json();
      
      if (!data.accessToken || !data.expiresAt) {
        throw new Error('Invalid token response from server');
      }

      const tokenPair: TokenPair = {
        accessToken: data.accessToken,
        expiresAt: data.expiresAt,
        tokenId: data.tokenId || crypto.randomUUID()
      };

      this.setTokens(tokenPair);
      
      return tokenPair;
    } catch (error) {
      // Don't clear valid tokens on refresh failure — refresh endpoint may not exist yet
      console.warn('[SecureTokenStorage] Token refresh failed:', error);
      return null;
    }
  }

  /**
   * Get valid access token, refreshing if necessary.
   * Graceful degradation: returns null if refresh fails.
   */
  static async getValidToken(): Promise<string | null> {
    const currentToken = this.getAccessToken();

    if (!currentToken) {
      // Try to refresh if we have no token
      const refreshed = await this.refreshTokens();
      return refreshed?.accessToken || null;
    }

    if (this.needsRefresh()) {
      const refreshed = await this.refreshTokens();
      return refreshed?.accessToken || currentToken; // Fall back to current if refresh fails
    }

    return currentToken;
  }

  /**
   * Revoke tokens on logout. Calls server to invalidate refresh token.
   */
  static async revokeTokens(): Promise<void> {
    try {
      const currentToken = memoryStore.accessToken;
      await fetch('/auth/revoke', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(currentToken ? { 'Authorization': `Bearer ${currentToken}` } : {})
        }
      });
    } catch (error) {
      console.warn('[SecureTokenStorage] Token revocation failed:', error);
    } finally {
      this.clear();
    }
  }

  /**
   * Clear old localStorage token backups from earlier builds.
   * Safe to call multiple times.
   */
  static initializeFromStorage(): void {
    try {
      localStorage.removeItem('garden_auth_secure_v2');
    } catch {
      // Ignore localStorage errors (e.g., private browsing mode)
    }
  }
}

// Legacy migration: clear any tokens from localStorage
(function migrateFromLocalStorage(): void {
  // Restore persisted secure token backup first.
  SecureTokenStorage.initializeFromStorage();

  const LEGACY_KEY = 'garden_auth';
  try {
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      // Parse and clear immediately
      localStorage.removeItem(LEGACY_KEY);
      if (import.meta.env.DEV) {
        console.log('[SecureTokenStorage] Migrated and cleared legacy localStorage tokens');
      }
    }
  } catch {
    // Ignore localStorage errors (e.g., private browsing mode)
  }
})();
